'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowUp, ChevronDown, EllipsisVertical, Trash2 } from 'lucide-react';
import HeadsetMic from '@mui/icons-material/HeadsetMic';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from '@practium/features/auth';
import { useFloraSupport } from '../../hooks/useFloraSupport';
import {
	FLORA_SUPPORT_ASSETS,
	FLORA_SUPPORT_COPY,
	FLORA_SUPPORT_SUGGESTIONS,
} from '../../constants/flora-support.constants';
import type { FloraSupportMessage } from '../../types/flora-support.types';
import styles from './FloraSupportWidget.module.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


const formatTime = (iso: string) =>
	new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const MessageBubble = ({ message }: { message: FloraSupportMessage }) => {
	const isUser = message.author === 'user';

	return (
		<div
			className={`${styles.messageBlock} ${isUser ? styles.messageBlockUser : styles.messageBlockSupport}`}
		>
			<div className={`${styles.bubble} ${isUser ? styles.bubbleUser : styles.bubbleSupport}`}>
				<div className={styles.bubbleText}>
					<ReactMarkdown remarkPlugins={[remarkGfm]}>
						{message.content}
					</ReactMarkdown>
				</div>

			</div>
			<time className={styles.timestamp} dateTime={message.createdAt}>
				{formatTime(message.createdAt)}
			</time>
		</div>
	);
};

export const FloraSupportWidget = () => {
	const { user, isAuthenticated } = useAuth();
	const userName = user?.name ?? '';
	const {
		isOpen,
		toggle,
		close,
		messages,
		isTyping,
		hasUserReplied,
		sendMessage,
		clearSession,
	} = useFloraSupport({
		userName,
		enabled: isAuthenticated,
	});
	const [draft, setDraft] = useState('');
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const bodyRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!bodyRef.current) return;
		bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
	}, [messages, isTyping, isOpen]);

	useEffect(() => {
		if (!isMenuOpen) return;

		const handlePointerDown = (event: PointerEvent) => {
			if (!menuRef.current?.contains(event.target as Node)) {
				setIsMenuOpen(false);
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsMenuOpen(false);
			}
		};

		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);

		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isMenuOpen]);

	if (!isAuthenticated) return null;

	const handleSend = () => {
		const value = draft.trim();
		if (!value) return;
		void sendMessage(value);
		setDraft('');
	};

	const handleMinimize = () => {
		setIsMenuOpen(false);
		close();
	};

	const handleClearSession = () => {
		clearSession();
		setDraft('');
		setIsMenuOpen(false);
	};

	return (
		<div className={styles.layer}>
			{isOpen && (
				<section className={styles.modal} aria-label={FLORA_SUPPORT_COPY.title}>
					<header className={styles.header}>
						<div className={styles.headerIcon} aria-hidden>
							<HeadsetMic />
						</div>
						<div className={styles.headerText}>
							<h2 className={styles.title}>{FLORA_SUPPORT_COPY.title}</h2>
							<p className={styles.subtitle}>
								<span className={styles.onlineDot} />
								{FLORA_SUPPORT_COPY.subtitle}
							</p>
						</div>
						<div className={styles.menuWrapper} ref={menuRef}>
							<button
								type="button"
								className={styles.menuTrigger}
								onClick={() => setIsMenuOpen((current) => !current)}
								aria-label={FLORA_SUPPORT_COPY.optionsLabel}
								aria-expanded={isMenuOpen}
								aria-haspopup="menu"
							>
								<EllipsisVertical />
							</button>
							{isMenuOpen && (
								<div className={styles.menu} role="menu">
									<button
										type="button"
										className={styles.menuItem}
										onClick={handleMinimize}
										role="menuitem"
									>
										<ChevronDown />
										<span>{FLORA_SUPPORT_COPY.minimizeLabel}</span>
									</button>
									<button
										type="button"
										className={styles.menuItem}
										onClick={handleClearSession}
										role="menuitem"
									>
										<Trash2 />
										<span>{FLORA_SUPPORT_COPY.clearSessionLabel}</span>
									</button>
								</div>
							)}
						</div>
						<button
							type="button"
							className={styles.minimize}
							onClick={close}
							aria-label={FLORA_SUPPORT_COPY.minimizeLabel}
						>
							<KeyboardArrowDown />
						</button>
					</header>

					<div className={styles.body} ref={bodyRef}>
						{messages.map((message) => (
							<MessageBubble key={message.id} message={message} />
						))}

						{isTyping && (
							<div className={styles.typing} role="status" aria-label="Flora está digitando">
								<span className={styles.typingDot} />
								<span className={styles.typingDot} />
								<span className={styles.typingDot} />
							</div>
						)}

						{!hasUserReplied && !isTyping && (
							<div className={styles.suggestions}>
								{FLORA_SUPPORT_SUGGESTIONS.map((suggestion) => (
									<button
										key={suggestion}
										type="button"
										className={styles.suggestion}
										onClick={() => void sendMessage(suggestion)}
									>
										{suggestion}
									</button>
								))}
							</div>
						)}
					</div>

					<footer className={styles.footer}>
						<div className={styles.composer}>
							<input
								className={styles.input}
								value={draft}
								onChange={(event) => setDraft(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === 'Enter' && !event.shiftKey) {
										event.preventDefault();
										handleSend();
									}
								}}
								placeholder={FLORA_SUPPORT_COPY.placeholder}
								aria-label={FLORA_SUPPORT_COPY.placeholder}
								autoComplete="off"
							/>
							<button
								type="button"
								className={styles.sendButton}
								onClick={handleSend}
								disabled={!draft.trim()}
								aria-label={FLORA_SUPPORT_COPY.sendLabel}
							>
								<ArrowUp strokeWidth={2.5} />
							</button>
						</div>
						<p className={styles.disclaimer}>{FLORA_SUPPORT_COPY.disclaimer}</p>
					</footer>
				</section>
			)}

			<button
				type="button"
				className={`${styles.fab} ${isOpen ? styles.fabOpen : ''}`}
				onClick={toggle}
				aria-label={isOpen ? FLORA_SUPPORT_COPY.fabCloseLabel : FLORA_SUPPORT_COPY.fabOpenLabel}
				aria-expanded={isOpen}
			>
				<img
					src={FLORA_SUPPORT_ASSETS.eyesClosed}
					alt=""
					className={`${styles.fabImage} ${styles.eyesClosed}`}
				/>
				<img
					src={FLORA_SUPPORT_ASSETS.eyesOpen}
					alt=""
					className={`${styles.fabImage} ${styles.eyesOpen}`}
				/>
				<img
					src={FLORA_SUPPORT_ASSETS.close}
					alt=""
					className={`${styles.fabImage} ${styles.closeIcon}`}
				/>
			</button>
		</div>
	);
};
